"use client";

import { useEffect, useState } from "react";

/**
 * Huset vårt som levende bakgrunn.
 *
 * Bildet er ikke bare lagt inn som background-image – det er bygget opp i lag
 * for å bli et stemningsbilde:
 *
 *   1. bloom    – kraftig uskarp, oppskalert kopi som lekker farge utover
 *   2. foto     – selve huset, lett gradert
 *   3. duotone  – fargelegging som binder bildet til appens palett
 *   4. lysglød  – sol/lys fra riktig retning etter tid på døgnet
 *   5. vignett  – mørkere kanter så innholdet over får ro
 *   6. korn     – filmkorn (SVG-støy) som fjerner det «rene» digitale
 *
 * Fargeleggingen følger tid på døgnet, så huset ser morgenlyst, ettermiddags-
 * gyllent eller nattblått ut. Det gjør at appen føles levende gjennom dagen.
 */

type TimeOfDay = "morgen" | "dag" | "kveld" | "natt";

function timeOfDay(hour: number): TimeOfDay {
  if (hour >= 5 && hour < 10) return "morgen";
  if (hour >= 10 && hour < 17) return "dag";
  if (hour >= 17 && hour < 22) return "kveld";
  return "natt";
}

/** Fargelegging per døgntid: [duotone-fra, duotone-til, glød, styrke] */
const GRADES: Record<
  TimeOfDay,
  { from: string; to: string; glow: string; duotone: number; dim: number }
> = {
  morgen: { from: "#fcd34d", to: "#38bdf8", glow: "#fef3c7", duotone: 0.34, dim: 0.14 },
  dag: { from: "#fbbf24", to: "#0ea5e9", glow: "#fffbeb", duotone: 0.26, dim: 0.1 },
  kveld: { from: "#fb923c", to: "#6366f1", glow: "#fed7aa", duotone: 0.42, dim: 0.24 },
  natt: { from: "#38bdf8", to: "#1e1b4b", glow: "#c7d2fe", duotone: 0.58, dim: 0.44 },
};

export function HouseBackdrop({ className = "" }: { className?: string }) {
  // Beregnes på klienten – server og klient kan ha ulik klokke/tidssone
  const [tod, setTod] = useState<TimeOfDay>("dag");

  useEffect(() => {
    const update = () => setTod(timeOfDay(new Date().getHours()));
    update();
    // Følg med hvis appen står åpen over et døgnskifte
    const id = setInterval(update, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const g = GRADES[tod];

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
    >
      {/* 1 – bloom: uskarp kopi som lekker farge ut i kantene */}
      <div className="house-bloom" />

      {/* 2 – huset */}
      <div className="house-photo" />

      {/* 3 – duotone som binder bildet til appens amber/blå palett */}
      <div
        className="absolute inset-0 mix-blend-color transition-opacity duration-1000"
        style={{
          opacity: g.duotone,
          backgroundImage: `linear-gradient(150deg, ${g.from} 0%, ${g.to} 100%)`,
        }}
      />

      {/* 4 – lysglød der sola står i bildet */}
      <div
        className="absolute inset-0 mix-blend-soft-light transition-opacity duration-1000"
        style={{
          opacity: 0.85,
          backgroundImage: `radial-gradient(60% 50% at 22% 12%, ${g.glow} 0%, transparent 70%)`,
        }}
      />

      {/* 5 – vignett + scrim: gir ro rundt innholdet som ligger over.
             Husveggen er lys, så uten dette forsvinner hvit tekst i fasaden. */}
      <div
        className="absolute inset-0 transition-opacity duration-1000"
        style={{
          background: `radial-gradient(115% 85% at 50% 30%, transparent 24%, rgba(10,8,6,${
            g.dim + 0.34
          }) 100%)`,
        }}
      />
      <div
        className="absolute inset-0 transition-opacity duration-1000"
        style={{ backgroundColor: `rgba(10,8,6,${g.dim})` }}
      />
      {/* Lett bånd så tittelen øverst og teksten nederst holder kontrast.
             Kortet er mørkt glass og klarer seg selv – dette er bare for teksten. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(10,8,6,0.3) 0%, transparent 30%, transparent 68%, rgba(10,8,6,0.34) 100%)",
        }}
      />

      {/* 6 – filmkorn */}
      <div className="house-grain" />
    </div>
  );
}
